/**
 * This is a fork of the `react-lazy-hydration` package to allow customization and
 * expand its features such as controlled hydration.
 */

import * as React from 'react';

const isBrowser = typeof window !== 'undefined';
const isDev = process.env.NODE_ENV === 'development';

export type LazyProps = {
  ssrOnly?: boolean;
  whenIdle?: boolean;
  whenVisible?: boolean | IntersectionObserverInit;
  noWrapper?: boolean | keyof JSX.IntrinsicElements;
  didHydrate?: VoidFunction;
  promise?: Promise<any>;
  on?: (keyof HTMLElementEventMap)[] | keyof HTMLElementEventMap;
  children: React.ReactElement;
  controlled?: boolean;
  rootElement?: any;
};

type Props = Omit<React.HTMLProps<HTMLElement>, 'dangerouslySetInnerHTML'> &
  LazyProps;

type VoidFunction = () => void;

// React currently throws a warning when using useLayoutEffect on the server.
const useIsomorphicLayoutEffect = isBrowser
  ? React.useLayoutEffect
  : React.useEffect;

function reducer() {
  return true;
}

function LazyHydrate(props: Props, ref: React.ForwardedRef<any>): JSX.Element {
  const childRef = React.useRef<HTMLElement>(null);

  // Always render on server
  const [hydrated, hydrate] = React.useReducer(reducer, !isBrowser);

  const {
    noWrapper,
    ssrOnly,
    whenIdle,
    whenVisible,
    promise, // pass a promise which hydrates
    on = [],
    children,
    didHydrate, // callback for hydration
    controlled,
    rootElement,
    ...rest
  } = props;

  if (
    isDev &&
    !ssrOnly &&
    !whenIdle &&
    !whenVisible &&
    !controlled &&
    !on.length &&
    !promise
  ) {
    // eslint-disable-next-line no-console
    console.error(
      `LazyHydration: Enable atleast one trigger for hydration.\n` +
        `If you don't want to hydrate, use ssrOnly`
    );
  }

  useIsomorphicLayoutEffect(() => {
    // No SSR Content
    if (!childRef.current.hasChildNodes()) {
      hydrate();
    }
  }, []);

  React.useEffect(() => {
    if (hydrated && didHydrate) {
      didHydrate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated]);

  React.useEffect(
    () => {
      if (ssrOnly || controlled || hydrated) return;

      const cleanupFns: VoidFunction[] = [];
      function cleanup() {
        cleanupFns.forEach((fn) => {
          fn();
        });
      }

      if (promise) {
        promise.then(hydrate, hydrate);
      }

      const getEventElement = () => {
        return rootElement || childRef.current;
      };

      const getIntersectionElement = () => {
        if (rootElement) return rootElement;
        if (noWrapper) {
          return childRef.current;
        }
        // As root node does not have any box model, it cannot intersect.
        return childRef.current.firstElementChild;
      };

      if (whenVisible) {
        const element = getIntersectionElement();

        if (element && typeof IntersectionObserver !== 'undefined') {
          const observerOptions =
            typeof whenVisible === 'object'
              ? whenVisible
              : {
                  rootMargin: '0px',
                };

          const io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting || entry.intersectionRatio > 0) {
                if (!hydrated) {
                  hydrate();
                }
              }
            });
          }, observerOptions);

          io.observe(element);

          cleanupFns.push(() => {
            io.disconnect();
          });
        } else {
          hydrate();
          return;
        }
      }
      if (whenIdle) {
        if (typeof requestIdleCallback !== 'undefined') {
          const idleCallbackId = requestIdleCallback(hydrate, { timeout: 500 });
          cleanupFns.push(() => {
            cancelIdleCallback(idleCallbackId);
          });
        } else {
          const id = setTimeout(hydrate, 2000);
          cleanupFns.push(() => {
            clearTimeout(id);
          });
        }
      }

      const events = ([] as Array<keyof HTMLElementEventMap>).concat(on);
      const element = getEventElement();

      events.forEach((event) => {
        element.addEventListener(event, hydrate, {
          once: true,
          passive: true,
        });
        cleanupFns.push(() => {
          element.removeEventListener(event, hydrate, {});
        });
      });
      // eslint-disable-next-line consistent-return
      return cleanup;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      on,
      rootElement,
      ssrOnly,
      whenIdle,
      whenVisible,
      didHydrate,
      promise,
      noWrapper,
      controlled,
    ]
  );

  React.useImperativeHandle(ref, () => ({ hydrate }));

  const WrapperElement = (typeof noWrapper === 'string'
    ? noWrapper
    : 'div') as unknown as React.FC<React.HTMLProps<HTMLElement>>;

  if (hydrated) {
    if (noWrapper) {
      return children;
    }
    return (
      <WrapperElement ref={childRef} style={{ display: 'contents' }} {...rest}>
        {children}
      </WrapperElement>
    );
  }
  return (
    <WrapperElement
      {...rest}
      ref={childRef}
      suppressHydrationWarning
      dangerouslySetInnerHTML={{ __html: '' }}
    />
  );
}

export default React.forwardRef(LazyHydrate);
